/**
 * The room's export for the render job — the .glb (5–20 MB) and the render
 * manifest — held in memory between Get Estimate (when the 3D view is still
 * there to export it) and the save (when the estimate has a guid to upload
 * it under). Never in React state or in the estimate's JSON: too big for
 * either. One at a time; the key pairs it with the estimate's preview.
 *
 * `hash` is the design's fingerprint (designFingerprint): the save sends it,
 * and the server queues nothing when it matches the render already made or
 * on its way (render-jobs-lib.php render_jobs_request).
 */

import type { RenderManifest } from '../../ui/room3d/sceneExport';

export interface RenderAsset {
  key: string;
  glb: ArrayBuffer;
  manifest: RenderManifest;
  hash?: string;
}

let held: RenderAsset | null = null;

export function holdRenderAsset(asset: RenderAsset): void {
  held = asset;
}

/** The asset for `key`, and it is no longer held; null when it is not the one held (or there is none). */
export function takeRenderAsset(key: string | undefined): RenderAsset | null {
  if (!key || !held || held.key !== key) return null;
  const asset = held;
  held = null;
  return asset;
}

/** Whether an asset is held for `key` (without taking it). */
export function hasRenderAsset(key: string | undefined): boolean {
  return !!key && !!held && held.key === key;
}

/** The held asset's design fingerprint for `key` (without taking it). */
export function heldRenderHash(key: string | undefined): string | undefined {
  return hasRenderAsset(key) ? held?.hash : undefined;
}

/** JSON with the keys of every object sorted, so the same design reads the same however it was built. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj).sort().filter((k) => obj[k] !== undefined).map((k) => `${JSON.stringify(k)}:${stableJson(obj[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/** cyrb53: a fast 53-bit string hash (not cryptographic — it only has to tell designs apart). */
function cyrb53(str: string, seed: number): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * What the render is of: the designer document (rooms, staging, finish) and the
 * manifest (camera, lights, look, render settings). The .glb is made from these,
 * so the same two give the same pictures. 28 hex characters.
 */
export function designFingerprint(doc: unknown, manifest: RenderManifest): string {
  const text = stableJson({ doc, manifest });
  return [cyrb53(text, 1), cyrb53(text, 2)].map((n) => n.toString(16).padStart(14, '0')).join('');
}
